import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Search, RefreshCw, FileText, Filter, ChevronLeft, ChevronRight, Eye, Download, Calendar as CalendarIcon, X, Archive, Activity, Database, Zap, Code } from 'lucide-react';
import { exportLogs } from '@/lib/logExporter';
import { toast } from 'sonner';
import { format, startOfDay, endOfDay, subDays } from 'date-fns';
import { cn } from '@/lib/utils';
import { LogArchiveViewer } from '@/components/LogArchiveViewer';
import { PayloadViewer } from '@/components/PayloadViewer';

interface ActivityLog {
  id: string;
  user_id: string | null;
  session_id: string | null;
  action_type: string;
  action_category: string;
  entity_type: string | null;
  entity_id: string | null;
  details: unknown;
  metadata: unknown;
  duration_ms: number | null;
  success: boolean;
  error_message: string | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  user_email?: string;
}

interface AdminLog {
  id: string;
  user_id: string | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  details: unknown;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  user_email?: string;
}

const ACTION_COLORS: Record<string, string> = {
  create: 'bg-green-500/10 text-green-600 border-green-500/20',
  insert: 'bg-green-500/10 text-green-600 border-green-500/20',
  update: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  delete: 'bg-red-500/10 text-red-600 border-red-500/20',
  login: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  logout: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
  error: 'bg-red-500/10 text-red-600 border-red-500/20',
  broadcast: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
  role_change: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  status_change: 'bg-cyan-500/10 text-cyan-600 border-cyan-500/20',
  user_created: 'bg-green-500/10 text-green-600 border-green-500/20',
  user_deleted: 'bg-red-500/10 text-red-600 border-red-500/20',
  // Activity log action types
  db_insert: 'bg-green-500/10 text-green-600 border-green-500/20',
  db_update: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  db_delete: 'bg-red-500/10 text-red-600 border-red-500/20',
  db_select: 'bg-slate-500/10 text-slate-600 border-slate-500/20',
  edge_function: 'bg-violet-500/10 text-violet-600 border-violet-500/20',
  page_view: 'bg-sky-500/10 text-sky-600 border-sky-500/20',
  route_change: 'bg-sky-500/10 text-sky-600 border-sky-500/20',
  form_submit: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  button_click: 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20',
  file_upload: 'bg-teal-500/10 text-teal-600 border-teal-500/20',
  file_download: 'bg-teal-500/10 text-teal-600 border-teal-500/20',
};

const CATEGORY_COLORS: Record<string, string> = {
  database: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  edge_function: 'bg-violet-500/10 text-violet-600 border-violet-500/20',
  navigation: 'bg-sky-500/10 text-sky-600 border-sky-500/20',
  interaction: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  file: 'bg-teal-500/10 text-teal-600 border-teal-500/20',
  auth: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  system: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
};

const ACTIVITY_CATEGORIES = [
  'all',
  'database',
  'edge_function',
  'navigation',
  'interaction',
  'file',
  'auth',
  'system',
];

const ENTITY_TYPES = [
  'all',
  'user',
  'claim',
  'property',
  'placement',
  'notification',
  'broadcast',
  'message',
  'auth',
  'system',
];

const PAGE_SIZE = 50;

export default function AdminLogsDashboard() {
  const [logs, setLogs] = useState<AdminLog[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [activitySearch, setActivitySearch] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [activityPage, setActivityPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [activityTotalCount, setActivityTotalCount] = useState(0);
  const [selectedLog, setSelectedLog] = useState<AdminLog | null>(null);
  const [selectedActivityLog, setSelectedActivityLog] = useState<ActivityLog | null>(null);
  const [exporting, setExporting] = useState(false);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [activityStartDate, setActivityStartDate] = useState<Date | undefined>(undefined);
  const [activityEndDate, setActivityEndDate] = useState<Date | undefined>(undefined);

  const fetchLogs = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      let query = supabase
        .from('admin_logs')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      if (entityFilter !== 'all') {
        query = query.eq('entity_type', entityFilter);
      }

      if (search) {
        query = query.or(`action.ilike.%${search}%,entity_type.ilike.%${search}%`);
      }

      if (startDate) {
        query = query.gte('created_at', startOfDay(startDate).toISOString());
      }

      if (endDate) {
        query = query.lte('created_at', endOfDay(endDate).toISOString());
      }

      const { data, error, count } = await query;

      if (error) throw error;

      // Fetch user emails for logs with user_ids
      const userIds = [...new Set(data?.filter(l => l.user_id).map(l => l.user_id) || [])];
      let userEmails: Record<string, string> = {};

      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, email')
          .in('id', userIds);

        profiles?.forEach(p => {
          userEmails[p.id] = p.email;
        });
      }

      const logsWithEmail = data?.map(log => ({
        ...log,
        user_email: log.user_id ? userEmails[log.user_id] : undefined,
      })) || [];

      setLogs(logsWithEmail);
      setTotalCount(count || 0);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
    setLoading(false);
      setRefreshing(false);
    }
  }, [page, entityFilter, search, startDate, endDate]);

  // Fetch activity logs
  const fetchActivityLogs = useCallback(async (isRefresh = false) => {
    if (!isRefresh) setActivityLoading(true);

    try {
      let query = supabase
        .from('activity_logs')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(activityPage * PAGE_SIZE, (activityPage + 1) * PAGE_SIZE - 1);

      if (categoryFilter !== 'all') {
        query = query.eq('action_category', categoryFilter);
      }

      if (activitySearch) {
        query = query.or(`action_type.ilike.%${activitySearch}%,entity_type.ilike.%${activitySearch}%`);
      }

      if (activityStartDate) {
        query = query.gte('created_at', startOfDay(activityStartDate).toISOString());
      }

      if (activityEndDate) {
        query = query.lte('created_at', endOfDay(activityEndDate).toISOString());
      }

      const { data, error, count } = await query;

      if (error) throw error;

      // Fetch user emails for logs with user_ids
      const userIds = [...new Set(data?.filter(l => l.user_id).map(l => l.user_id) || [])];
      let userEmails: Record<string, string> = {};

      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, email')
          .in('id', userIds);

        profiles?.forEach(p => {
          userEmails[p.id] = p.email;
        });
      }

      const logsWithEmail = data?.map(log => ({
        ...log,
        user_email: log.user_id ? userEmails[log.user_id] : undefined,
      })) || [];

      setActivityLogs(logsWithEmail);
      setActivityTotalCount(count || 0);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    } finally {
      setActivityLoading(false);
    }
  }, [activityPage, categoryFilter, activitySearch, activityStartDate, activityEndDate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchActivityLogs();
  }, [fetchActivityLogs]);

  // Real-time subscription for admin logs
  useEffect(() => {
    const channel = supabase
      .channel('admin-logs-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'admin_logs',
        },
        (payload) => {
          const newLog = payload.new as AdminLog;
          setLogs(prev => [newLog, ...prev.slice(0, PAGE_SIZE - 1)]);
          setTotalCount(prev => prev + 1);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Real-time subscription for activity logs
  useEffect(() => {
    const channel = supabase
      .channel('activity-logs-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'activity_logs',
        },
        (payload) => {
          const newLog = payload.new as ActivityLog;
          setActivityLogs(prev => [newLog, ...prev.slice(0, PAGE_SIZE - 1)]);
          setActivityTotalCount(prev => prev + 1);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const getActionColor = (action: string) => {
    const lowerAction = action.toLowerCase();
    for (const [key, color] of Object.entries(ACTION_COLORS)) {
      if (lowerAction.includes(key)) return color;
    }
    return 'bg-gray-500/10 text-gray-600 border-gray-500/20';
  };

  const getCategoryColor = (category: string) => {
    return CATEGORY_COLORS[category] || 'bg-gray-500/10 text-gray-600 border-gray-500/20';
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const activityTotalPages = Math.ceil(activityTotalCount / PAGE_SIZE);

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const handleActivitySearch = (value: string) => {
    setActivitySearch(value);
    setActivityPage(0);
  };

  const handleEntityFilter = (value: string) => {
    setEntityFilter(value);
    setPage(0);
  };

  const handleCategoryFilter = (value: string) => {
    setCategoryFilter(value);
    setActivityPage(0);
  };

  const handleDatePreset = (preset: 'today' | 'week' | 'month' | 'clear') => {
    const now = new Date();
    switch (preset) {
      case 'today':
        setStartDate(now);
        setEndDate(now);
        break;
      case 'week':
        setStartDate(subDays(now, 7));
        setEndDate(now);
        break;
      case 'month':
        setStartDate(subDays(now, 30));
        setEndDate(now);
        break;
      case 'clear':
        setStartDate(undefined);
        setEndDate(undefined);
        break;
    }
    setPage(0);
  };

  const handleActivityDatePreset = (preset: 'today' | 'week' | 'month' | 'clear') => {
    const now = new Date();
    switch (preset) {
      case 'today':
        setActivityStartDate(now);
        setActivityEndDate(now);
        break;
      case 'week':
        setActivityStartDate(subDays(now, 7));
        setActivityEndDate(now);
        break;
      case 'month':
        setActivityStartDate(subDays(now, 30));
        setActivityEndDate(now);
        break;
      case 'clear':
        setActivityStartDate(undefined);
        setActivityEndDate(undefined);
        break;
    }
    setActivityPage(0);
  };

  const handleExport = async (exportFormat: 'csv' | 'json') => {
    setExporting(true);
    try {
      // Fetch all logs matching current filters (up to 10000 for export)
      let query = supabase
        .from('admin_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10000);

      if (entityFilter !== 'all') {
        query = query.eq('entity_type', entityFilter);
      }

      if (search) {
        query = query.or(`action.ilike.%${search}%,entity_type.ilike.%${search}%`);
      }

      if (startDate) {
        query = query.gte('created_at', startOfDay(startDate).toISOString());
      }

      if (endDate) {
        query = query.lte('created_at', endOfDay(endDate).toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;

      // Fetch user emails
      const userIds = [...new Set(data?.filter(l => l.user_id).map(l => l.user_id) || [])];
      let userEmails: Record<string, string> = {};

      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, email')
          .in('id', userIds);

        profiles?.forEach(p => {
          userEmails[p.id] = p.email;
        });
      }

      const logsWithEmail = data?.map(log => ({
        ...log,
        user_email: log.user_id ? userEmails[log.user_id] : undefined,
      })) || [];

      exportLogs(logsWithEmail, exportFormat);
      toast.success(`Exported ${logsWithEmail.length} logs as ${exportFormat.toUpperCase()}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export logs');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">System Logs</h1>
        <p className="text-muted-foreground">View, search, and manage system activity logs</p>
      </div>

      <Tabs defaultValue="activity" className="space-y-6">
        <TabsList>
          <TabsTrigger value="activity" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Activity Logs
          </TabsTrigger>
          <TabsTrigger value="admin" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Admin Logs
          </TabsTrigger>
          <TabsTrigger value="archives" className="flex items-center gap-2">
            <Archive className="h-4 w-4" />
            Archived Logs
          </TabsTrigger>
        </TabsList>

        {/* Activity Logs Tab */}
        <TabsContent value="activity" className="space-y-6">
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchActivityLogs(true)}
              disabled={activityLoading}
            >
              {activityLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              <span className="ml-2">Refresh</span>
            </Button>
          </div>

          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search activity logs..."
                      value={activitySearch}
                      onChange={(e) => handleActivitySearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={categoryFilter} onValueChange={handleCategoryFilter}>
                    <SelectTrigger className="w-[180px]">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Filter by category" />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIVITY_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>
                          {cat === 'all' ? 'All Categories' : cat.replace('_', ' ').charAt(0).toUpperCase() + cat.replace('_', ' ').slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date Range Filters */}
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-muted-foreground whitespace-nowrap">Date Range:</Label>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleActivityDatePreset('today')}>
                        Today
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleActivityDatePreset('week')}>
                        Last 7 days
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleActivityDatePreset('month')}>
                        Last 30 days
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-1">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className={cn(
                            "w-[140px] justify-start text-left font-normal",
                            !activityStartDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {activityStartDate ? format(activityStartDate, "MMM d, yyyy") : "Start date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={activityStartDate}
                          onSelect={(date) => { setActivityStartDate(date); setActivityPage(0); }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>

                    <span className="text-muted-foreground">to</span>

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className={cn(
                            "w-[140px] justify-start text-left font-normal",
                            !activityEndDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {activityEndDate ? format(activityEndDate, "MMM d, yyyy") : "End date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={activityEndDate}
                          onSelect={(date) => { setActivityEndDate(date); setActivityPage(0); }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>

                    {(activityStartDate || activityEndDate) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleActivityDatePreset('clear')}
                        className="h-8 px-2"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Activity Logs Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                User Activity Log
              </CardTitle>
              <CardDescription>
                Showing {activityLogs.length} of {activityTotalCount.toLocaleString()} activity logs
              </CardDescription>
            </CardHeader>
            <CardContent>
              {activityLoading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : activityLogs.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                  <Activity className="h-12 w-12 mb-4 opacity-50" />
                  <p>No activity logs found</p>
                  <p className="text-sm">User activities will appear here as they occur</p>
                </div>
              ) : (
                <>
                  <ScrollArea className="h-[500px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[150px]">Timestamp</TableHead>
                          <TableHead>Action</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Entity</TableHead>
                          <TableHead>User</TableHead>
                          <TableHead>Duration</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Details</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activityLogs.map((log) => (
                          <TableRow key={log.id}>
                            <TableCell className="font-mono text-xs text-muted-foreground">
                              {format(new Date(log.created_at), 'MMM d HH:mm:ss')}
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant="outline" 
                                className={getActionColor(log.action_type)}
                              >
                                {log.action_type}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant="outline" 
                                className={getCategoryColor(log.action_category)}
                              >
                                {log.action_category}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="font-medium text-sm">{log.entity_type || '-'}</span>
                                {log.entity_id && (
                                  <span className="text-xs text-muted-foreground font-mono truncate max-w-[100px]">
                                    {log.entity_id.length > 20 ? log.entity_id.substring(0, 20) + '...' : log.entity_id}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {log.user_email ? (
                                <span className="text-sm truncate max-w-[120px] block">{log.user_email}</span>
                              ) : log.user_id ? (
                                <span className="text-xs text-muted-foreground font-mono">
                                  {log.user_id.substring(0, 8)}...
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-sm">Anonymous</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm">
                              {log.duration_ms ? `${log.duration_ms}ms` : '-'}
                            </TableCell>
                            <TableCell>
                              <Badge variant={log.success ? "default" : "destructive"} className="text-xs">
                                {log.success ? "Success" : "Failed"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setSelectedActivityLog(log)}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                                  <DialogHeader>
                                    <DialogTitle className="flex items-center gap-2">
                                      <Code className="h-5 w-5" />
                                      Activity Log Details
                                    </DialogTitle>
                                    <DialogDescription>
                                      {format(new Date(log.created_at), 'MMMM d, yyyy HH:mm:ss')}
                                    </DialogDescription>
                                  </DialogHeader>
                                  <ScrollArea className="flex-1 overflow-auto">
                                    <div className="space-y-6 pr-4">
                                      {/* Summary Grid */}
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div>
                                          <p className="text-xs font-medium text-muted-foreground mb-1">Action Type</p>
                                          <Badge variant="outline" className={getActionColor(log.action_type)}>
                                            {log.action_type}
                                          </Badge>
                                        </div>
                                        <div>
                                          <p className="text-xs font-medium text-muted-foreground mb-1">Category</p>
                                          <Badge variant="outline" className={getCategoryColor(log.action_category)}>
                                            {log.action_category}
                                          </Badge>
                                        </div>
                                        <div>
                                          <p className="text-xs font-medium text-muted-foreground mb-1">Status</p>
                                          <Badge variant={log.success ? "default" : "destructive"}>
                                            {log.success ? "Success" : "Failed"}
                                          </Badge>
                                        </div>
                                        {log.duration_ms && (
                                          <div>
                                            <p className="text-xs font-medium text-muted-foreground mb-1">Duration</p>
                                            <p className="text-sm font-mono">{log.duration_ms}ms</p>
                                          </div>
                                        )}
                                      </div>

                                      {/* Entity Info */}
                                      <div className="grid grid-cols-2 gap-4">
                                        {log.entity_type && (
                                          <div>
                                            <p className="text-xs font-medium text-muted-foreground mb-1">Entity Type</p>
                                            <p className="text-sm">{log.entity_type}</p>
                                          </div>
                                        )}
                                        {log.entity_id && (
                                          <div>
                                            <p className="text-xs font-medium text-muted-foreground mb-1">Entity ID</p>
                                            <p className="font-mono text-sm break-all">{log.entity_id}</p>
                                          </div>
                                        )}
                                        {log.user_id && (
                                          <div>
                                            <p className="text-xs font-medium text-muted-foreground mb-1">User</p>
                                            <p className="text-sm">{log.user_email || log.user_id}</p>
                                          </div>
                                        )}
                                        {log.session_id && (
                                          <div>
                                            <p className="text-xs font-medium text-muted-foreground mb-1">Session ID</p>
                                            <p className="font-mono text-xs break-all">{log.session_id}</p>
                                          </div>
                                        )}
                                      </div>

                                      {/* Error Message */}
                                      {log.error_message && (
                                        <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                                          <p className="text-xs font-medium text-destructive mb-1">Error Message</p>
                                          <p className="text-sm text-destructive">{log.error_message}</p>
                                        </div>
                                      )}

                                      {/* Payload Viewer */}
                                      {log.details && typeof log.details === 'object' && Object.keys(log.details as object).length > 0 && (
                                        <div>
                                          <p className="text-sm font-medium text-muted-foreground mb-3">Payload Data</p>
                                          <PayloadViewer details={log.details} />
                                        </div>
                                      )}

                                      {/* User Agent */}
                                      {log.user_agent && (
                                        <div className="pt-4 border-t">
                                          <p className="text-xs font-medium text-muted-foreground mb-1">User Agent</p>
                                          <p className="text-xs text-muted-foreground break-all">{log.user_agent}</p>
                                        </div>
                                      )}
                                    </div>
                                  </ScrollArea>
                                </DialogContent>
                              </Dialog>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>

                  {/* Pagination */}
                  <div className="flex items-center justify-between pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Page {activityPage + 1} of {activityTotalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setActivityPage(p => Math.max(0, p - 1))}
                        disabled={activityPage === 0}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setActivityPage(p => Math.min(activityTotalPages - 1, p + 1))}
                        disabled={activityPage >= activityTotalPages - 1}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Admin Logs Tab (renamed from current) */}
        <TabsContent value="admin" className="space-y-6">
          <div className="flex items-center justify-end gap-2">
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={exporting || totalCount === 0}>
                {exporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                <span className="ml-2">Export</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('csv')}>
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('json')}>
                Export as JSON
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchLogs(true)}
            disabled={refreshing}
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="ml-2">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search logs..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={entityFilter} onValueChange={handleEntityFilter}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  {ENTITY_TYPES.map(type => (
                    <SelectItem key={type} value={type}>
                      {type === 'all' ? 'All Types' : type.charAt(0).toUpperCase() + type.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Range Filters */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground whitespace-nowrap">Date Range:</Label>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleDatePreset('today')}>
                    Today
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDatePreset('week')}>
                    Last 7 days
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDatePreset('month')}>
                    Last 30 days
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-1">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        "w-[140px] justify-start text-left font-normal",
                        !startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, "MMM d, yyyy") : "Start date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={(date) => { setStartDate(date); setPage(0); }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <span className="text-muted-foreground">to</span>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        "w-[140px] justify-start text-left font-normal",
                        !endDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, "MMM d, yyyy") : "End date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={(date) => { setEndDate(date); setPage(0); }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                {(startDate || endDate) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDatePreset('clear')}
                    className="h-8 px-2"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Activity Log
          </CardTitle>
          <CardDescription>
            Showing {logs.length} of {totalCount.toLocaleString()} logs
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <FileText className="h-12 w-12 mb-4 opacity-50" />
              <p>No logs found</p>
              <p className="text-sm">Try adjusting your filters</p>
            </div>
          ) : (
            <>
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[180px]">Timestamp</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Entity</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead className="text-right">Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {format(new Date(log.created_at), 'MMM d, yyyy HH:mm:ss')}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className={getActionColor(log.action)}
                          >
                            {log.action}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium">{log.entity_type}</span>
                            {log.entity_id && (
                              <span className="text-xs text-muted-foreground font-mono">
                                {log.entity_id.substring(0, 8)}...
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {log.user_email ? (
                            <span className="text-sm">{log.user_email}</span>
                          ) : log.user_id ? (
                            <span className="text-xs text-muted-foreground font-mono">
                              {log.user_id.substring(0, 8)}...
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-sm">System</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedLog(log)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl">
                              <DialogHeader>
                                <DialogTitle>Log Details</DialogTitle>
                                <DialogDescription>
                                  {format(new Date(log.created_at), 'MMMM d, yyyy HH:mm:ss')}
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <p className="text-sm font-medium text-muted-foreground">Action</p>
                                    <Badge variant="outline" className={getActionColor(log.action)}>
                                      {log.action}
                                    </Badge>
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-muted-foreground">Entity Type</p>
                                    <p>{log.entity_type}</p>
                                  </div>
                                  {log.entity_id && (
                                    <div className="col-span-2">
                                      <p className="text-sm font-medium text-muted-foreground">Entity ID</p>
                                      <p className="font-mono text-sm">{log.entity_id}</p>
                                    </div>
                                  )}
                                  {log.user_id && (
                                    <div className="col-span-2">
                                      <p className="text-sm font-medium text-muted-foreground">User</p>
                                      <p>{log.user_email || log.user_id}</p>
                                    </div>
                                  )}
                                  {log.ip_address && (
                                    <div>
                                      <p className="text-sm font-medium text-muted-foreground">IP Address</p>
                                      <p className="font-mono text-sm">{log.ip_address}</p>
                                    </div>
                                  )}
                                </div>
                                {log.details && typeof log.details === 'object' && Object.keys(log.details as object).length > 0 && (
                                  <div>
                                    <p className="text-sm font-medium text-muted-foreground mb-2">Details</p>
                                    <pre className="bg-muted p-4 rounded-lg overflow-auto text-xs">
                                      {JSON.stringify(log.details, null, 2)}
                                    </pre>
                                  </div>
                                )}
                                {log.user_agent && (
                                  <div>
                                    <p className="text-sm font-medium text-muted-foreground mb-1">User Agent</p>
                                    <p className="text-xs text-muted-foreground break-all">{log.user_agent}</p>
                                  </div>
                                )}
                              </div>
                            </DialogContent>
                          </Dialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>

              {/* Pagination */}
              <div className="flex items-center justify-between pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Page {page + 1} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="archives">
          <LogArchiveViewer />
        </TabsContent>
      </Tabs>
    </div>
  );
}
